import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Key, AlertTriangle, CheckCircle2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The recovery UI renders the user's ACTUAL stored questions after step 1.
// Users pick 5 from a larger pool during signup, so any hardcoded list
// silently failed recovery when it didn't match a given user (iter 118 fix).

const TwoFactorRecoveryModal = ({ isOpen, onClose, onSuccess, defaultIndos = '' }) => {
  const [step, setStep] = useState(1); // 1: Credentials, 2: Security Questions, 3: Success
  const [indosNumber, setIndosNumber] = useState(defaultIndos);
  const [password, setPassword] = useState('');
  // Live list of the user's actual questions — fetched from backend after
  // step 1. Starts empty; hydrated by handleCredentialsNext.
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  // Persistent failure state — surfaces a "Contact Support" escape hatch
  // when either the answers were wrong or the account is throttled. Toasts
  // alone are too easy to miss + they don't offer an action.
  const [recoveryError, setRecoveryError] = useState(null); // { message, kind }
  const navigate = useNavigate();

  const handleCredentialsNext = async () => {
    if (!indosNumber || indosNumber.length !== 8) {
      toast.error('Please enter a valid 8-character INDoS number');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    try {
      setLoading(true);
      // Fetch this user's ACTUAL stored security questions.
      const resp = await axios.post(`${API}/auth/2fa/recover/get-questions`, {
        indos_number: indosNumber.toUpperCase(),
        password,
        // Backend ignores security_answers on this endpoint but the shared
        // Recover2FARequest model requires the field. Empty list is fine.
        security_answers: [],
      });
      const questions = resp.data?.questions || [];
      if (questions.length !== 5) {
        toast.error('Security questions unavailable. Contact support.');
        return;
      }
      setAnswers(questions.map((q) => ({ question: q, answer: '' })));
      setStep(2);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to load recovery questions';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index].answer = value;
    setAnswers(newAnswers);
    // Clear inline error the moment the user starts correcting answers.
    if (recoveryError && recoveryError.kind === 'wrong_answers') {
      setRecoveryError(null);
    }
  };

  const handleContactSupport = () => {
    onClose();
    navigate('/help');
  };

  const handleRecover = async () => {
    // Validate all answers are filled
    const emptyAnswers = answers.filter(a => !a.answer.trim());
    if (emptyAnswers.length > 0) {
      toast.error('Please answer all security questions');
      return;
    }

    try {
      setLoading(true);
      setRecoveryError(null);
      await axios.post(`${API}/auth/2fa/recover`, {
        indos_number: indosNumber.toUpperCase(),
        password: password,
        security_answers: answers
      });

      setStep(3);
      toast.success('2FA has been disabled. You can now login.');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Recovery failed';

      // Categorise so we can surface a persistent Contact Support CTA
      // where it actually helps (throttled + repeated failures).
      let kind = 'other';
      let message = detail;
      if (detail.includes('Too many recovery attempts')) {
        kind = 'locked';
      } else if (detail.toLowerCase().includes('security answers')) {
        kind = 'wrong_answers';
        message = 'One or more security answers are incorrect. Please try again — or contact support if you don\'t remember them.';
      }
      setRecoveryError({ message, kind });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setIndosNumber(defaultIndos);
    setPassword('');
    setAnswers([]);
    setRecoveryError(null);
    onClose();
  };

  const handleSuccessClose = () => {
    handleClose();
    onSuccess?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-[#0A7EA4]" />
            {step === 1 && 'Recover 2FA Access'}
            {step === 2 && 'Security Questions'}
            {step === 3 && 'Recovery Successful'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Enter your credentials to start the recovery process'}
            {step === 2 && 'Answer ALL your security questions to disable 2FA'}
            {step === 3 && 'Your two-factor authentication has been disabled'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Credentials */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold">Recovery requires all 5 security questions</p>
                  <p>This is to ensure maximum security for your account.</p>
                </div>
              </div>
            </div>

            <div>
              <Label>INDoS Number</Label>
              <Input
                type="text"
                maxLength={8}
                placeholder="e.g., 17EL1129"
                value={indosNumber}
                onChange={(e) => setIndosNumber(e.target.value.toUpperCase())}
                className="mt-2 uppercase"
              />
            </div>

            <div>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCredentialsNext()}
                className="mt-2"
              />
            </div>
          </div>
        )}

        {/* Step 2: Security Questions — render THIS user's actual questions
            (loaded in step 1), not a hardcoded list. */}
        {step === 2 && (
          <div className="space-y-4">
            {recoveryError && (
              <div
                className={`rounded-md border p-3 text-sm ${
                  recoveryError.kind === 'locked'
                    ? 'border-rose-200 bg-rose-50 text-rose-800'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
                data-testid="recovery-failure-banner"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p>{recoveryError.message}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleContactSupport}
                        className="h-7 px-2 text-xs bg-white hover:bg-slate-50"
                        data-testid="recovery-contact-support-btn"
                      >
                        <LifeBuoy className="w-3.5 h-3.5 mr-1" />
                        Contact Support
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {answers.map((a, index) => (
              <div key={index}>
                <Label className="text-sm">
                  {index + 1}. {a.question}
                </Label>
                <Input
                  type="text"
                  placeholder="Your answer"
                  value={a.answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  className="mt-1"
                  data-testid={`recovery-answer-${index}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">2FA Disabled Successfully</h3>
            <p className="text-gray-600 mb-4">
              You can now login with just your INDoS number and password.
            </p>
            <p className="text-sm text-gray-500">
              We recommend setting up 2FA again once you have access to an authenticator app.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleCredentialsNext} disabled={loading}>
                {loading ? 'Loading...' : 'Next: Security Questions'}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleRecover} disabled={loading}>
                {loading ? 'Recovering...' : 'Recover Access'}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={handleSuccessClose} className="w-full">
              Continue to Login
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorRecoveryModal;
